import React from 'react';

import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';
import {connect} from 'react-redux';

import {HYDROPHOBIC_ALIAS, SUNGLASSES_ALIAS} from 'constants/upsells';
import {useGetActualPhone} from 'desktop/hooks/useGetActualPhone/useGetActualPhone';
import {ApplicationStoreType} from 'desktop/store';
import {VoidFunctionType} from 'types/types';

import {addUpsellItemAction, removeUpsellItemAction} from '../../store/reducers/upsells/upsells';
import {canAddUpsell, isCurrentCartItemSunglasses, makeGetItemUpsells} from '../../store/selectors';
import {UpsellOfferType} from '../../types';

import {Upsell} from '../upsells/upsells';
import {UpsellType} from '../upsellsList/upsellsList';

import s from './upsellOffer.scss';

export type PropsType = {
    upsells: Array<UpsellOfferType>;
    itemId: string;
    removeUpsellHandler: (data: {id: string; name: string}) => void;
    addUpsellHandler: (data: {id: string; name: string}) => void;
    qty: number;
    isCanAddUpsell: boolean;
    inStockQty: number;
    isSunglasses: boolean;
};

/**
 * Component for cart upsells
 */
export const CartUpsellOffer: React.FC<PropsType> = ({
    addUpsellHandler,
    inStockQty,
    isCanAddUpsell,
    isSunglasses,
    itemId,
    qty,
    removeUpsellHandler,
    upsells = [],
}) => {
    const phone = useGetActualPhone();

    /**
     * Toggle upsell
     * @param {UpsellOfferType} upsell - toggled upsell
     * @returns {VoidFunctionType} function toggling upsell from argument
     */
    const toggleUpsell =
        ({alias, isAdded}: UpsellOfferType): VoidFunctionType =>
        () => {
            if (!isCanAddUpsell) {
                return;
            }

            if (isAdded) {
                removeUpsellHandler({id: itemId, name: alias});
                pushDataLayer({event: `cart_btn_upsell ${alias} remove`});
            } else {
                addUpsellHandler({id: itemId, name: alias});
                pushDataLayer({event: `cart_btn_upsell ${alias} add`});
            }
        };

    /**
     * Render upsells
     */
    const renderUpsells = () => {
        return upsells.map((upsell: UpsellOfferType) => {
            const isNotShowSunglassesUpsell =
                upsell.alias === SUNGLASSES_ALIAS &&
                ((!upsell.isAdded && qty > 1) || inStockQty < 2);

            const upsellCommonProps = {
                ...upsell,
                phone,
                toggleUpsell: toggleUpsell(upsell),
            } as UpsellType;

            const isHydrophobicForSG = isSunglasses && upsell.alias === HYDROPHOBIC_ALIAS;

            if (isNotShowSunglassesUpsell || isHydrophobicForSG) {
                return null;
            }

            return <Upsell {...upsellCommonProps} itemId={itemId} key={upsell.alias} />;
        });
    };

    const upsellsContent = renderUpsells();

    return upsellsContent.length > 0 ? (
        <div className={s.upsells} data-test-name="upsells">
            {upsellsContent}
        </div>
    ) : null;
};

/**
 * Map state to props
 * @param {ApplicationStoreType} state - redux store
 * @param {PropsType} props
 * @returns {PropsType} props
 */
const mapStateToProps = (state: ApplicationStoreType, props: Partial<PropsType>) => ({
    isCanAddUpsell: canAddUpsell(state),
    isSunglasses: isCurrentCartItemSunglasses(state, props.itemId as string),
    upsells: makeGetItemUpsells()(state, props.itemId as string),
});

const mapDispatchToProps = {
    addUpsellHandler: addUpsellItemAction,
    removeUpsellHandler: removeUpsellItemAction,
};

export const UpsellOfferConnected = connect(mapStateToProps, mapDispatchToProps)(CartUpsellOffer);
